/* eslint-disable react/prop-types */
import { forwardRef, useState } from 'react';
import { Field, getIn } from 'formik';
import { Dropdown, Form } from 'react-bootstrap';
import { Star, StarFill } from 'react-bootstrap-icons';
import {
  setIssueFavorite,
  unsetIssueFavorite,
} from '../../utils/API/issues';

const FAVORITES_GROUP_LABEL = '⭐ PREFERITI';

const buildGroups = ({ userProjects, userIssues, isFutureMonth }) => {
  const projectById = new Map(userProjects.map((p) => [p.id, p]));
  const groups = [];

  const decorate = (issue) => {
    const project = projectById.get(issue.projectId);
    return {
      ...issue,
      projectSummary: project ? project.summary : '',
      disabled: isFutureMonth && !(project && project.canHaveIssuesInFuture),
    };
  };

  const favorites = userIssues
    .filter((i) => i.isFavorite && projectById.has(i.projectId))
    .map(decorate);

  if (favorites.length > 0) {
    groups.push({
      key: 'favorites',
      label: FAVORITES_GROUP_LABEL,
      showProjectPrefix: true,
      items: favorites,
    });
  }

  userProjects.forEach((project) => {
    const items = userIssues
      .filter((i) => i.projectId === project.id && !i.isFavorite)
      .map(decorate);
    if (items.length === 0) return;
    groups.push({
      key: `project_${project.id}`,
      label: project.summary,
      showProjectPrefix: false,
      items,
    });
  });

  return groups;
};

const SelectLikeToggle = forwardRef(
  ({ children, onClick, disabled, isInvalid, isPlaceholder }, ref) => (
    <button
      ref={ref}
      type="button"
      disabled={disabled}
      onClick={(e) => {
        e.preventDefault();
        onClick(e);
      }}
      className={`form-select text-start${isInvalid ? ' is-invalid' : ''}`}
      style={{
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        color: isPlaceholder ? '#6c757d' : undefined,
      }}
    >
      {children}
    </button>
  )
);
SelectLikeToggle.displayName = 'SelectLikeToggle';

const ActivitySelectInner = ({
  field,
  form,
  userProjects,
  userIssues,
  setUserIssues,
  isFutureMonth,
  disabled,
  label,
}) => {
  const [open, setOpen] = useState(false);
  const errorMsg = getIn(form.errors, field.name);
  const touched = getIn(form.touched, field.name);
  const isInvalid = Boolean(errorMsg && touched);

  const groups = buildGroups({ userProjects, userIssues, isFutureMonth });

  const selectedIssue = userIssues.find(
    (i) => String(i.id) === String(field.value)
  );
  const selectedProject = selectedIssue
    ? userProjects.find((p) => p.id === selectedIssue.projectId)
    : null;
  const toggleText = selectedIssue
    ? `${selectedProject ? selectedProject.summary : ''} — ${
        selectedIssue.summary
      }`
    : 'Seleziona ...';

  const handleSelect = (issue) => {
    form.setFieldTouched(field.name, true, false);
    form.setFieldValue(field.name, String(issue.id), true);
    setOpen(false);
  };

  const toggleFavorite = async (e, issue) => {
    e.stopPropagation();
    e.preventDefault();
    const newValue = !issue.isFavorite;
    setUserIssues((prev) =>
      prev.map((i) =>
        i.id === issue.id ? { ...i, isFavorite: newValue } : i
      )
    );
    try {
      if (newValue) {
        await setIssueFavorite(issue.id);
      } else {
        await unsetIssueFavorite(issue.id);
      }
    } catch (err) {
      setUserIssues((prev) =>
        prev.map((i) =>
          i.id === issue.id ? { ...i, isFavorite: !newValue } : i
        )
      );
    }
  };

  return (
    <Form.Group controlId={`form${field.name}`} className="form-group">
      <Form.Label>{label}</Form.Label>
      <Dropdown
        show={open}
        onToggle={(next) => setOpen(next)}
        autoClose="outside"
      >
        <Dropdown.Toggle
          as={SelectLikeToggle}
          disabled={disabled}
          isInvalid={isInvalid}
          isPlaceholder={!selectedIssue}
        >
          {toggleText}
        </Dropdown.Toggle>
        <Dropdown.Menu
          className="w-100"
          style={{ maxHeight: '320px', overflowY: 'auto' }}
        >
          {groups.length === 0 && (
            <Dropdown.ItemText className="text-muted">
              Nessuna attività disponibile
            </Dropdown.ItemText>
          )}
          {groups.map((group) => (
            <div key={group.key}>
              <Dropdown.Header>{group.label}</Dropdown.Header>
              {group.items.map((issue) => (
                <Dropdown.Item
                  key={`${group.key}_${issue.id}`}
                  as="button"
                  type="button"
                  active={
                    selectedIssue &&
                    selectedIssue.id === issue.id
                  }
                  disabled={issue.disabled}
                  onClick={() => handleSelect(issue)}
                  className="d-flex align-items-center"
                >
                  <span style={{ flex: 1, minWidth: 0 }}>
                    {group.showProjectPrefix ? (
                      <>
                        <span style={{ color: '#666' }}>
                          {issue.projectSummary}
                        </span>
                        <span style={{ margin: '0 6px', color: '#999' }}>
                          —
                        </span>
                        {issue.summary}
                      </>
                    ) : (
                      issue.summary
                    )}
                  </span>
                  <span
                    role="button"
                    tabIndex={-1}
                    aria-label={
                      issue.isFavorite
                        ? 'Rimuovi dai preferiti'
                        : 'Aggiungi ai preferiti'
                    }
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onClick={(e) => toggleFavorite(e, issue)}
                    style={{
                      padding: '0 0.25rem',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                    }}
                  >
                    {issue.isFavorite ? (
                      <StarFill color="#f5b301" />
                    ) : (
                      <Star color="#999" />
                    )}
                  </span>
                </Dropdown.Item>
              ))}
            </div>
          ))}
        </Dropdown.Menu>
      </Dropdown>
      {isInvalid && (
        <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
          {errorMsg}
        </Form.Control.Feedback>
      )}
    </Form.Group>
  );
};

const ActivitySelect = ({
  name,
  label,
  userProjects,
  userIssues,
  setUserIssues,
  isFutureMonth,
  disabled,
}) => (
  <Field name={name}>
    {({ field, form }) => (
      <ActivitySelectInner
        field={field}
        form={form}
        userProjects={userProjects}
        userIssues={userIssues}
        setUserIssues={setUserIssues}
        isFutureMonth={isFutureMonth}
        disabled={disabled}
        label={label}
      />
    )}
  </Field>
);

export default ActivitySelect;
